import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Card,
  Button,
  Alert,
  Spinner,
  Table,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  startDatabaseBackupJob,
  getDatabaseBackupJobStatus,
  downloadJobBackupFile,
  createJobBackupDownloadLink,
  createSavedBackupDownloadLink,
  triggerNativeBackupDownload,
  listSavedBackups,
  type SavedBackupItem,
} from '../services/databaseBackup.service';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import {
  createBackupManualDownloadUrl,
  deliverBackupBlob,
  revokeBackupManualDownloadUrl,
  sanitizeDownloadFilename,
  type DeliverBackupMethod,
} from '../utils/downloadBlob';

const BACKUP_POLL_MS = 3000;
const BACKUP_WARN_AFTER_SEC = 600;
const BACKUP_GIVE_UP_SEC = 3600;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds} 秒`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m} 分 ${s} 秒`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h} 時間 ${rm} 分`;
}

function BackupManualDownloadLink({
  url,
  filename,
  className,
}: {
  url: string;
  filename: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const safeName = sanitizeDownloadFilename(filename);
  return (
    <p className={className ?? 'small mb-0 mt-2'}>
      {t('db_backup.manual_link_hint', {
        defaultValue: 'ダウンロード一覧に表示されない場合:',
      })}{' '}
      <a href={url} download={safeName} className="fw-semibold">
        {t('db_backup.manual_link_label', {
          defaultValue: '{{filename}} を保存',
          filename: safeName,
        })}
      </a>
    </p>
  );
}

type OnDemandPhase =
  | { kind: 'idle' }
  | { kind: 'creating'; jobId: string; elapsedSec: number; slow: boolean }
  | { kind: 'ready'; jobId: string; filename: string; size: number }
  | { kind: 'receiving'; jobId: string; filename: string; size: number }
  | { kind: 'fetched'; filename: string; size: number; manualUrl: string }
  | { kind: 'saving'; filename: string; size: number; manualUrl: string }
  | {
      kind: 'saved';
      filename: string;
      method: DeliverBackupMethod;
      manualUrl: string;
    }
  | { kind: 'failed'; message: string };

function revokePhaseManualUrl(phase: OnDemandPhase): void {
  if (phase.kind === 'fetched' || phase.kind === 'saving' || phase.kind === 'saved') {
    revokeBackupManualDownloadUrl(phase.manualUrl);
  }
}

function resolveApiErrorMessage(
  err: unknown,
  fallback: string,
  t: (key: string, options?: { defaultValue?: string }) => string
): string {
  if (!(err instanceof Error)) {
    return fallback;
  }
  const keyed = err as Error & { translationKey?: string };
  if (keyed.translationKey) {
    return t(keyed.translationKey, { defaultValue: keyed.message || fallback });
  }
  if (keyed.message.startsWith('api.error.')) {
    return t(keyed.message, { defaultValue: fallback });
  }
  return keyed.message || fallback;
}

function useOnDemandBackup() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<OnDemandPhase>({ kind: 'idle' });
  const [directDownloadLoading, setDirectDownloadLoading] = useState(false);
  const [directDownloadError, setDirectDownloadError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const fetchedBlobRef = useRef<Blob | null>(null);
  const manualUrlRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(
    () => () => {
      revokeBackupManualDownloadUrl(manualUrlRef.current);
    },
    []
  );

  const readyJobId = phase.kind === 'ready' || phase.kind === 'receiving' ? phase.jobId : null;

  const downloadDirectJob = useCallback(
    async (jobId: string) => {
      setDirectDownloadLoading(true);
      setDirectDownloadError(null);
      try {
        const link = await createJobBackupDownloadLink(jobId);
        triggerNativeBackupDownload(link.href, link.filename);
      } catch (err: unknown) {
        setDirectDownloadError(
          resolveApiErrorMessage(
            err,
            t('db_backup.error_direct_link', {
              defaultValue:
                '直接ダウンロードに失敗しました。「別手段: Blob で受信」をお試しください。',
            }),
            t
          )
        );
      } finally {
        setDirectDownloadLoading(false);
      }
    },
    [t]
  );

  const fetchReadyFile = useCallback(async () => {
    if (phase.kind !== 'ready') return;
    const { jobId, filename, size } = phase;
    setPhase({ kind: 'receiving', jobId, filename, size });
    try {
      const { blob, filename: fetchedName } = await downloadJobBackupFile(jobId);
      if (blob.size === 0) {
        throw new Error(
          t('db_backup.error_empty_file', { defaultValue: 'バックアップファイルが空です。' })
        );
      }
      fetchedBlobRef.current = blob;
      const manualUrl = createBackupManualDownloadUrl(blob);
      manualUrlRef.current = manualUrl;
      setPhase({
        kind: 'fetched',
        filename: fetchedName || filename,
        size: blob.size,
        manualUrl,
      });
    } catch (err: unknown) {
      fetchedBlobRef.current = null;
      const httpStatus =
        err && typeof err === 'object' && 'httpStatus' in err
          ? Number((err as { httpStatus?: number }).httpStatus)
          : undefined;
      setPhase({
        kind: 'failed',
        message:
          httpStatus === 404 || httpStatus === 409
            ? t('db_backup.error_job_file_missing', {
                defaultValue:
                  'バックアップファイルが見つかりません。上の日次バックアップからダウンロードするか、もう一度取得してください。',
              })
            : err instanceof Error
              ? err.message
              : t('db_backup.error_backup', { defaultValue: 'バックアップの取得に失敗しました。' }),
      });
    }
  }, [phase, t]);

  const saveFetchedFile = useCallback(async () => {
    if (phase.kind !== 'fetched') return;
    const blob = fetchedBlobRef.current;
    if (!blob || blob.size === 0) {
      setPhase({
        kind: 'failed',
        message: t('db_backup.error_empty_file', { defaultValue: 'バックアップファイルが空です。' }),
      });
      return;
    }
    const { filename, size, manualUrl: prevManualUrl } = phase;
    setPhase({ kind: 'saving', filename, size, manualUrl: prevManualUrl });
    try {
      const result = await deliverBackupBlob(blob, filename);
      fetchedBlobRef.current = null;
      if (prevManualUrl !== result.manualUrl) {
        revokeBackupManualDownloadUrl(prevManualUrl);
      }
      manualUrlRef.current = result.manualUrl;
      const nextPhase: OnDemandPhase = {
        kind: 'saved',
        filename,
        method: result.method,
        manualUrl: result.manualUrl,
      };
      window.requestAnimationFrame(() => {
        setPhase(nextPhase);
      });
    } catch (err: unknown) {
      setPhase({
        kind: 'failed',
        message:
          err instanceof Error
            ? err.message
            : t('db_backup.error_backup', { defaultValue: 'バックアップの保存に失敗しました。' }),
      });
    }
  }, [phase, t]);

  const pollOnce = useCallback(
    async (jobId: string) => {
      const status = await getDatabaseBackupJobStatus(jobId);
      const slow = status.elapsedSec >= BACKUP_WARN_AFTER_SEC;

      if (status.status === 'ready') {
        stopPolling();
        setPhase({
          kind: 'ready',
          jobId,
          filename: status.filename || `kensuke_backup_${Date.now()}.sql`,
          size: status.size ?? 0,
        });
        return;
      }

      if (status.status === 'failed') {
        stopPolling();
        setPhase({
          kind: 'failed',
          message:
            status.error ||
            t('db_backup.error_backup', { defaultValue: 'バックアップの作成に失敗しました。' }),
        });
        return;
      }

      if (status.elapsedSec >= BACKUP_GIVE_UP_SEC) {
        stopPolling();
        setPhase({
          kind: 'failed',
          message: t('db_backup.error_timeout', {
            defaultValue:
              '1時間を超えてもバックアップが完了しませんでした。上の「自動バックアップ」日次をご利用ください。',
          }),
        });
        return;
      }

      setPhase({ kind: 'creating', jobId, elapsedSec: status.elapsedSec, slow });
    },
    [stopPolling, t]
  );

  const start = useCallback(async () => {
    stopPolling();
    setDirectDownloadError(null);
    setPhase((prev) => {
      revokePhaseManualUrl(prev);
      manualUrlRef.current = null;
      fetchedBlobRef.current = null;
      return { kind: 'idle' };
    });
    try {
      const jobId = await startDatabaseBackupJob();
      setPhase({ kind: 'creating', jobId, elapsedSec: 0, slow: false });
      await pollOnce(jobId);
      pollRef.current = window.setInterval(() => {
        void pollOnce(jobId);
      }, BACKUP_POLL_MS);
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : t('db_backup.error_backup', { defaultValue: 'バックアップの開始に失敗しました。' });
      setPhase({ kind: 'failed', message: msg });
    }
  }, [pollOnce, stopPolling, t]);

  const reset = useCallback(() => {
    stopPolling();
    setDirectDownloadError(null);
    setPhase((prev) => {
      revokePhaseManualUrl(prev);
      manualUrlRef.current = null;
      fetchedBlobRef.current = null;
      return { kind: 'idle' };
    });
  }, [stopPolling]);

  const isBusy =
    phase.kind === 'creating' || phase.kind === 'receiving' || phase.kind === 'saving';

  return {
    phase,
    start,
    reset,
    fetchReadyFile,
    saveFetchedFile,
    downloadDirectJob,
    isBusy,
    directDownloadLoading,
    directDownloadError,
    readyJobId,
  };
}

export default function DatabaseBackupRestore() {
  const { t } = useTranslation();
  const onDemand = useOnDemandBackup();

  const [savedDownloadingPath, setSavedDownloadingPath] = useState<string | null>(null);
  const [savedDownloadError, setSavedDownloadError] = useState<string | null>(null);

  const { data: savedList, isLoading: savedListLoading } = useQuery({
    queryKey: ['admin-database-backup-list'],
    queryFn: listSavedBackups,
  });

  const handleDirectDownloadSaved = async (relativePath: string) => {
    setSavedDownloadError(null);
    setSavedDownloadingPath(relativePath);
    try {
      const link = await createSavedBackupDownloadLink(relativePath);
      triggerNativeBackupDownload(link.href, link.filename);
    } catch (err: unknown) {
      setSavedDownloadError(
        err instanceof Error
          ? err.message
          : t('db_backup.error_backup', { defaultValue: 'バックアップの取得に失敗しました。' })
      );
    } finally {
      setSavedDownloadingPath(null);
    }
  };

  const renderSavedSuccessMessage = (filename: string, method: DeliverBackupMethod) => {
    if (method === 'picker') {
      return t('db_backup.file_saved_picker', {
        defaultValue: 'ファイルを保存しました: {{filename}}',
        filename,
      });
    }
    return t('db_backup.download_started', {
      defaultValue:
        'ダウンロードを開始しました: {{filename}}。ブラウザのダウンロード一覧を確認してください。',
      filename,
      size: '',
    });
  };

  const renderBackupTable = (items: SavedBackupItem[]) => (
    <Table size="sm" bordered>
      <thead>
        <tr>
          <th>{t('db_backup.col_date', { defaultValue: '日付' })}</th>
          <th>{t('db_backup.col_size', { defaultValue: 'サイズ' })}</th>
          <th>{t('db_backup.col_action', { defaultValue: '操作' })}</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? (
          <tr>
            <td colSpan={3} className="text-muted">
              {t('db_backup.no_saved', { defaultValue: 'なし' })}
            </td>
          </tr>
        ) : (
          items.map((item) => (
            <tr key={item.path}>
              <td>{item.dateLabel}</td>
              <td>{formatBytes(item.size)}</td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={savedDownloadingPath === item.path}
                    onClick={() => void handleDirectDownloadSaved(item.path)}
                  >
                    {savedDownloadingPath === item.path ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" aria-hidden />
                        {t('db_backup.preparing', { defaultValue: '準備中…' })}
                      </>
                    ) : (
                      t('db_backup.download_saved_btn', { defaultValue: 'ダウンロード' })
                    )}
                  </Button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );

  return (
    <Container className="py-4">
      <h1 className="mb-4">{t('db_backup.title', { defaultValue: 'データベース バックアップ・手動復旧' })}</h1>
      <p className="text-muted mb-4">
        {t('db_backup.description', {
          defaultValue:
            '手動復旧用 SQL バンドルをダウンロードできます。復旧は psql -f による手動実行のみです（管理画面からは実行しません）。管理者のみが閲覧・操作できます。',
        })}
      </p>

      <CollapsibleHelpSection
        titleKey="db_backup.help_title"
        storageKey="db_backup"
        descriptionKey="db_backup.help_content"
      />

      {/* 1. 自動バックアップ（推奨） */}
      <Card className="mb-4 border-primary">
        <Card.Header className="bg-primary bg-opacity-10">
          {t('db_backup.saved_title', { defaultValue: '保存済みバックアップ（自動）' })}
          <span className="ms-2 badge text-bg-primary">
            {t('db_backup.recommended', { defaultValue: '推奨' })}
          </span>
        </Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            {t('db_backup.saved_help', {
              defaultValue:
                '毎日0時に自動実行されます。「ダウンロード」で Chrome のダウンロード一覧に SQL が保存されます。',
            })}
          </p>
          {savedDownloadError && <Alert variant="danger">{savedDownloadError}</Alert>}
          {savedListLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <Tabs defaultActiveKey="daily" className="mb-0">
              <Tab eventKey="daily" title={t('db_backup.tab_daily', { defaultValue: '日次（7日分）' })}>
                {renderBackupTable(savedList?.daily ?? [])}
              </Tab>
              <Tab eventKey="weekly" title={t('db_backup.tab_weekly', { defaultValue: '週次（5週分）' })}>
                {renderBackupTable(savedList?.weekly ?? [])}
              </Tab>
              <Tab eventKey="monthly" title={t('db_backup.tab_monthly', { defaultValue: '月次（12ヶ月分）' })}>
                {renderBackupTable(savedList?.monthly ?? [])}
              </Tab>
              <Tab eventKey="yearly" title={t('db_backup.tab_yearly', { defaultValue: '年次（10年分）' })}>
                {renderBackupTable(savedList?.yearly ?? [])}
              </Tab>
            </Tabs>
          )}
        </Card.Body>
      </Card>

      {/* 2. 今すぐバックアップ */}
      <Card className="mb-4">
        <Card.Header>
          {t('db_backup.backup_title', { defaultValue: '今すぐバックアップ' })}
        </Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            {t('db_backup.backup_help_simple', {
              defaultValue:
                '「バックアップを取得」後、「直接ダウンロード（推奨）」を押すと Chrome のダウンロード一覧に SQL が保存されます。',
            })}
          </p>

          {onDemand.phase.kind === 'failed' && (
            <Alert variant="danger" className="mb-3">
              {onDemand.phase.message}
            </Alert>
          )}

          {(onDemand.phase.kind === 'ready' || onDemand.phase.kind === 'receiving') && (
            <Alert variant="success" className="mb-3">
              <p className="mb-2 fw-semibold">
                {t('db_backup.ready_title', { defaultValue: 'バックアップの準備ができました' })}
              </p>
              <p className="small mb-3">
                {t('db_backup.ready_detail', {
                  defaultValue:
                    '{{filename}}（{{size}}）。まず「直接ダウンロード」を押してください（Chrome のダウンロード一覧に表示されます）。',
                  filename: onDemand.phase.filename,
                  size: formatBytes(onDemand.phase.size),
                })}
              </p>
              {onDemand.directDownloadError && (
                <Alert variant="warning" className="py-2 small mb-2">
                  {onDemand.directDownloadError}{' '}
                  {onDemand.phase.kind === 'ready' && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 align-baseline"
                      disabled={onDemand.directDownloadLoading}
                      onClick={() => {
                        // クロージャ内で再度ナローイング（ready 以外では何もしない）
                        if (onDemand.phase.kind === 'ready') {
                          void onDemand.downloadDirectJob(onDemand.phase.jobId);
                        }
                      }}
                    >
                      {t('common.retry', { defaultValue: '再試行' })}
                    </Button>
                  )}
                </Alert>
              )}
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {onDemand.readyJobId ? (
                  <Button
                    variant="success"
                    disabled={onDemand.directDownloadLoading || onDemand.phase.kind === 'receiving'}
                    onClick={() => void onDemand.downloadDirectJob(onDemand.readyJobId!)}
                  >
                    {onDemand.directDownloadLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                        {t('db_backup.preparing', { defaultValue: '準備中…' })}
                      </>
                    ) : (
                      t('db_backup.direct_download_btn', { defaultValue: '直接ダウンロード（推奨）' })
                    )}
                  </Button>
                ) : null}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => void onDemand.fetchReadyFile()}
                  disabled={onDemand.phase.kind === 'receiving'}
                >
                  {onDemand.phase.kind === 'receiving' ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                      {t('db_backup.receiving', { defaultValue: '受信中…' })}
                    </>
                  ) : (
                    t('db_backup.fetch_file_btn', { defaultValue: '別手段: Blob で受信' })
                  )}
                </Button>
              </div>
            </Alert>
          )}

          {(onDemand.phase.kind === 'fetched' || onDemand.phase.kind === 'saving') && (
            <Alert variant="success" className="mb-3">
              <p className="small mb-3">
                {t('db_backup.fetched_detail', {
                  defaultValue:
                    '{{filename}}（{{size}}）を受信しました。「ブラウザに保存」を押すか、下のリンクから保存してください。',
                  filename: onDemand.phase.filename,
                  size: formatBytes(onDemand.phase.size),
                })}
              </p>
              <Button
                variant="outline-success"
                disabled={onDemand.phase.kind === 'saving'}
                onClick={() => void onDemand.saveFetchedFile()}
              >
                {onDemand.phase.kind === 'saving' ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                    {t('db_backup.saving', { defaultValue: '保存中…' })}
                  </>
                ) : (
                  t('db_backup.save_file_btn', { defaultValue: '2. ブラウザに保存' })
                )}
              </Button>
              <BackupManualDownloadLink
                url={onDemand.phase.manualUrl}
                filename={onDemand.phase.filename}
              />
            </Alert>
          )}

          {onDemand.phase.kind === 'saved' && (
            <Alert variant="success" className="mb-3">
              <p className="small mb-0">
                {renderSavedSuccessMessage(onDemand.phase.filename, onDemand.phase.method)}
              </p>
              <BackupManualDownloadLink
                url={onDemand.phase.manualUrl}
                filename={onDemand.phase.filename}
              />
            </Alert>
          )}

          {onDemand.phase.kind === 'creating' && (
            <Alert variant={onDemand.phase.slow ? 'warning' : 'info'} className="mb-3" role="status">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Spinner animation="border" size="sm" aria-hidden />
                <strong>
                  {t('db_backup.progress_background', {
                    defaultValue: 'サーバーでバックアップを作成しています',
                  })}
                </strong>
              </div>
              <p className="small mb-0">
                {t('db_backup.progress_elapsed', {
                  defaultValue: '経過時間: {{elapsed}}',
                  elapsed: formatElapsed(onDemand.phase.elapsedSec),
                })}
                {onDemand.phase.slow &&
                  ` — ${t('db_backup.progress_slow_warning', {
                    defaultValue: '10分を超えています。上の日次バックアップの利用もご検討ください。',
                  })}`}
              </p>
            </Alert>
          )}

          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => void onDemand.start()}
              disabled={
                onDemand.phase.kind === 'creating' ||
                onDemand.phase.kind === 'receiving' ||
                onDemand.phase.kind === 'ready' ||
                onDemand.phase.kind === 'fetched' ||
                onDemand.phase.kind === 'saving'
              }
            >
              {onDemand.phase.kind === 'creating' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  {t('db_backup.preparing', { defaultValue: '作成中…' })}
                </>
              ) : (
                t('db_backup.download_backup', { defaultValue: 'バックアップを取得' })
              )}
            </Button>
            {onDemand.phase.kind !== 'idle' &&
              onDemand.phase.kind !== 'creating' &&
              onDemand.phase.kind !== 'receiving' &&
              onDemand.phase.kind !== 'saving' && (
              <Button variant="outline-secondary" onClick={onDemand.reset}>
                {t('db_backup.reset_on_demand', { defaultValue: 'リセット' })}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* 3. 手動復旧手順 */}
      <Card className="mb-4 border-warning">
        <Card.Header className="bg-warning bg-opacity-10">
          {t('db_backup.manual_restore_title', { defaultValue: '手動復旧手順（psql）' })}
        </Card.Header>
        <Card.Body>
          <Alert variant="warning" className="small">
            {t('db_backup.manual_restore_warning', {
              defaultValue:
                'ダウンロードした SQL には DROP SCHEMA public CASCADE が含まれます。接続先 DB 名を必ず確認してから実行してください。本番 DB への誤実行はデータ全損失になります。',
            })}
          </Alert>
          <ol className="small mb-3">
            <li>{t('db_backup.manual_restore_step1', { defaultValue: '実行前に現行 DB のバックアップを取得する' })}</li>
            <li>
              {t('db_backup.manual_restore_step2', {
                defaultValue: '接続先が開発 (kensuke) か本番 (kensuke-prod) かを確認する',
              })}
            </li>
            <li>
              {t('db_backup.manual_restore_step3', {
                defaultValue: '下記コマンドで SQL を適用する（ファイル名はダウンロードした kensuke_manual_restore_*.sql）',
              })}
            </li>
            <li>{t('db_backup.manual_restore_step4', { defaultValue: 'Prisma クライアント再生成とバックエンド再起動' })}</li>
          </ol>
          <pre className="bg-light border rounded p-3 small mb-0">
            {`psql -U postgres -d kensuke -v ON_ERROR_STOP=1 -f kensuke_manual_restore_YYYYMMDD_HHMMSS.sql
cd backend && npm run prisma:generate
# PM2 再起動（本番 VPS の場合は ssh new_kuu21@153.126.170.14 上で実行）`}
          </pre>
          <p className="text-muted small mt-3 mb-0">
            {t('db_backup.manual_restore_verify', {
              defaultValue:
                '復旧後は psql で件数確認（例: SELECT COUNT(*) FROM sm_samples;）や /samples の表示を確認してください。',
            })}
          </p>
        </Card.Body>
      </Card>
    </Container>
  );
}
